import type React from 'react'
import { useSelector } from 'react-redux'
import InfoBox from '../../../../../../components/InfoBox'
import Table, {
  type SchemaItem,
} from '../../../../../../components/Table/Table'
import { setVeliClientsTableState } from '../../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../../redux/store'
import type {
  VeliClientActivationStatus,
  VeliClientImportStatus,
} from '../../../../../../types/coreApi-types'

// A flat row in the Veli clients table. The table component keys on `id`; a seeded Veli client has no
// Penning user id yet, so the (unique) Veli user id stands in. The wealth-manager name is only carried at
// the top level (clients across every book listed together); a single WM's sub-page hides that column.
export type VeliClientRow = VeliClientActivationStatus & {
  id: string
  wealthManagerName?: string | null
}

const ClientStatusBadge: React.FC<{ status: VeliClientImportStatus }> = ({
  status,
}) => (
  <div className="w-min">
    <InfoBox colorScheme={status === 'Activated' ? 'green' : 'gray'}>
      {status === 'Activated' ? 'Activated' : 'Not activated'}
    </InfoBox>
  </div>
)

interface VeliClientsTableProps {
  clients: VeliClientRow[]
  isLoading?: boolean
  // Show the wealth-manager column. Off on a single WM's sub-page (where it's constant).
  showWealthManager?: boolean
}

// The imported Veli clients as a flat, sortable table — the readable replacement for the old grouped
// activation-funnel sections. Read-only: a seeded Veli client is not yet a Penning user, so there is no
// detail page to drill into (a row click is a no-op).
const VeliClientsTable: React.FC<VeliClientsTableProps> = ({
  clients,
  isLoading,
  showWealthManager = true,
}) => {
  const tableState = useSelector((state: RootState) => state.veliClientsTable)

  const allColumns: SchemaItem<VeliClientRow>[] = [
    {
      cardTitle: true,
      header: 'Name',
      accessorKey: 'name',
      type: 'string',
      priority: 1,
    },
    {
      header: 'Email',
      accessorKey: 'email',
      type: 'string',
      priority: 2,
    },
    {
      header: 'Wealth manager',
      accessorKey: 'wealthManagerName',
      type: 'string',
      priority: 3,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      type: 'status',
      priority: 4,
      cellValueWrapper: (_value, _originalValue, { original }) => (
        <ClientStatusBadge status={original.status} />
      ),
    },
  ]

  const schema = allColumns.filter(
    item => showWealthManager || item.accessorKey !== 'wealthManagerName'
  )

  return (
    <Table<VeliClientRow>
      tableState={tableState}
      setTableState={setVeliClientsTableState}
      navigate={() => null}
      isLoading={isLoading}
      onClickRow={() => null}
      emptyDataRender={<div className="text-xl mb-3">No Veli clients found</div>}
      defaultData={clients}
      schema={schema}
    />
  )
}

export default VeliClientsTable
